import { useMemo, useState } from "react"
import { Button, Box, Flex, HStack, Text, VStack, Link, Input } from "@chakra-ui/react"
import { type ColumnDef } from "@tanstack/react-table"
import DataTable from "../../../framework/DataTable"
import ErrorAlert from "../../../components/ErrorAlert"
import { useResortFeatures, type SkiFeatureSummary } from "../../../hooks/useResortFeatures"
import { extractErrorInfo } from "../../../lib/api/error-utils"

type ResortFeaturesTabProps = {
  resortUid: string
}

export default function ResortFeaturesTab({ resortUid }: ResortFeaturesTabProps) {
  const [featureFilter, setFeatureFilter] = useState("")
  const {
    data: featurePages,
    isLoading: featuresLoading,
    error: featuresError,
    fetchNextPage: fetchNextFeatures,
    hasNextPage,
    isFetchingNextPage: loadingMoreFeatures,
  } = useResortFeatures({
    resortUid,
    filter: featureFilter || undefined,
    limit: 10,
  })

  const featureRows = useMemo(
    () => featurePages?.pages.flatMap((page) => page.features) ?? [],
    [featurePages]
  )

  const featuresErrorInfo = featuresError ? extractErrorInfo(featuresError) : null

  const featureColumns = useMemo<ColumnDef<SkiFeatureSummary>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <Link
            href={`/resorts/${resortUid}/features/${row.original.uid}`}
            color="blue.200"
            fontWeight="semibold"
            _hover={{ textDecoration: "underline" }}
          >
            {row.original.name || row.original.uid}
          </Link>
        ),
      },
      { accessorKey: "type", header: "Type" },
      { accessorKey: "difficulty", header: "Difficulty" },
      { accessorKey: "difficulty_convention", header: "Convention" },
      {
        accessorKey: "is_backcountry",
        header: "Backcountry",
        cell: ({ getValue }) => (getValue<boolean>() ? "Yes" : "No"),
      },
    ],
    [resortUid]
  )

  return (
    <VStack align="stretch" gap="3">
      <HStack justify="space-between" align="center" flexWrap="wrap" gap="3">
        <Text fontWeight="semibold">Runs & lifts</Text>
        <Input
          value={featureFilter}
          onChange={(event) => setFeatureFilter(event.target.value)}
          placeholder="Filter by name"
          bg="gray.700"
          borderColor="gray.600"
          borderRadius="md"
          px="3"
          py="2"
          fontSize="sm"
          type="text"
        />
      </HStack>
      {featuresErrorInfo && <ErrorAlert message={featuresErrorInfo.message} traceId={featuresErrorInfo.traceId} />}
      <Box overflowX="auto">
        <DataTable
          columns={featureColumns}
          data={featureRows}
          isLoading={featuresLoading}
          emptyMessage="No runs or lifts found for this resort."
        />
      </Box>
      {hasNextPage && (
        <Flex justify="flex-end">
          <Button
            size="sm"
            colorPalette="blue"
            onClick={() => fetchNextFeatures()}
            loading={loadingMoreFeatures}
            disabled={loadingMoreFeatures}
          >
            {loadingMoreFeatures ? "Loading..." : "Load more"}
          </Button>
        </Flex>
      )}
    </VStack>
  )
}
